import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { DropDownMenuOption } from '@/components/dropdown-menu-option';
import { FlatList, Modal, TouchableWithoutFeedback, View } from 'react-native';
import { FONTS, shadow } from '@/styles';
import { TrackingData } from '@/utils/analytics/base-reporter';
import { Typography } from '@usepepper/ui/atoms/typography';

const HOURS = Array.from({ length: 24 }, (_, i) => i); // 0-23 hours
const ITEM_HEIGHT = 40; // Height of each time option item
const ITEMS_TO_DISPLAY = 3; // Number of items to display in the dropdown

interface Props {
  date?: Date;
  minimumDate?: Date;
  minuteInterval?: number;
  onChange: (date?: Date) => void;
  trackingData: TrackingData | null;
}

const formatTimeOption = (hour: number, minute: number) => {
  return hour >= 12
    ? `${(hour === 12 ? hour : hour - 12).toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} PM`
    : `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} AM`;
};

export const TimePickerInput: React.FC<Props> = ({
  date,
  onChange,
  minuteInterval = 1,
  minimumDate,
  trackingData,
}) => {
  const triggerRef = useRef<View>(null);
  const flatListRef = useRef<FlatList<string>>(null);
  const [styles] = useStyles();
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ width: 0, x: 0, y: 0 });
  const [dropDownWidth, setDropDownWidth] = useState(150); // Default width for dropdown

  const timeOptions = useMemo(() => {
    const options = [];
    const availableMinutes = Array.from({ length: 60 }, (_, i) => i).filter(
      (minute) => minute % minuteInterval === 0,
    );
    // Adjust the minimum date to the nearest upper minuteInterval
    const minDate = minimumDate ? new Date(minimumDate) : undefined;
    if (minDate && minuteInterval > 1) {
      minDate.setMinutes(
        Math.ceil(minDate.getMinutes() / minuteInterval) * minuteInterval,
      );
    }
    // Calculate the minimum hour and minute based on the minimum date
    const minHour = minDate ? minDate.getHours() : 0;
    const selectedHour = date ? date.getHours() : minHour;
    let minMinute = 0;
    if (minDate && minHour === selectedHour) {
      minMinute = minDate.getMinutes();
    }
    for (let hour of HOURS) {
      if (hour < minHour) {
        continue; // Skip hours before the minimum hour
      }
      for (let minute of availableMinutes) {
        if (hour === minHour && minute < minMinute) {
          continue; // Skip minutes before the minimum minute for the current hour
        }
        options.push(formatTimeOption(hour, minute));
      }
    }
    return options;
  }, [minuteInterval, date, minimumDate]);

  useEffect(() => {
    if (triggerRef.current && visible) {
      triggerRef.current.measure((_fx, _fy, width, height, px, py) => {
        setPosition({
          width: width,
          x: px,
          y: py + height,
        });
      });
    }
  }, [visible]);

  // Make dropdown responsive proportional to the trigger button width
  useEffect(() => {
    if (triggerRef.current) {
      triggerRef.current.measure((_fx, _fy, width) => {
        setDropDownWidth(width);
      });
    }
  }, [triggerRef]);

  const onSelect = (option: string) => {
    const [timeStr, amPm] = option.split(' ');
    const [hour, minute] = timeStr.split(':').map(Number);

    const newTime = new Date();
    newTime.setHours(amPm === 'AM' || hour === 12 ? hour : hour + 12);
    newTime.setMinutes(minute, 0, 0);
    onChange(newTime);
    setVisible(false);
  };

  useEffect(() => {
    if (visible && date && flatListRef.current && timeOptions.length > 0) {
      const selectedOption = formatTimeOption(
        date.getHours(),
        date.getMinutes(),
      );
      const selectedIndex = timeOptions.findIndex(
        (opt) => opt === selectedOption,
      );
      if (selectedIndex !== -1) {
        flatListRef.current.scrollToIndex({
          animated: false,
          index: selectedIndex,
          viewPosition: 0.5,
        });
      }
    }
  }, [visible, date, timeOptions]);

  const renderItem = ({ item }: { item: string }) => (
    <DropDownMenuOption
      key={item}
      onSelect={() => onSelect(item)}
      style={styles.menuItem}
    >
      <Typography variant="paragraph-base">{item}</Typography>
    </DropDownMenuOption>
  );

  const getItemLayout = (
    _data: ArrayLike<string> | null | undefined,
    index: number,
  ) => ({
    index,
    length: ITEM_HEIGHT,
    offset: ITEM_HEIGHT * index,
  });

  return (
    <>
      <Button
        hoverStyle={styles.buttonHoverStyle}
        onPress={() => setVisible(!visible)}
        ref={triggerRef}
        size="large"
        style={styles.buttonStyle}
        textStyle={FONTS.primary}
        trackingData={trackingData}
        type="secondary"
      >
        {date ? formatTimeOption(date.getHours(), date.getMinutes()) : 'HH:MM'}
      </Button>
      {visible ? (
        <Modal
          animationType="fade"
          onRequestClose={() => setVisible(false)}
          transparent
          visible={visible}
        >
          <TouchableWithoutFeedback onPress={() => setVisible(false)}>
            <View style={styles.modalOverlay}>
              <FlatList
                data={timeOptions}
                getItemLayout={getItemLayout}
                initialNumToRender={2 * ITEMS_TO_DISPLAY}
                keyExtractor={(item) => item}
                ref={flatListRef}
                renderItem={renderItem}
                style={[
                  styles.menu,
                  {
                    left: position.x + position.width / 2 - dropDownWidth / 2,
                    top: position.y,
                    width: dropDownWidth,
                  },
                ]}
                windowSize={5}
              />
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  buttonHoverStyle: {
    borderColor: theme.sk.color.border.input.active,
  },
  buttonStyle: {
    alignItems: 'flex-start',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.input.default,
    borderRadius: 8,
    borderWidth: 1,
    height: 48,
    paddingHorizontal: 16,
  },
  menu: {
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    elevation: 5,
    fontFamily: theme.fonts.primary,
    maxHeight: ITEMS_TO_DISPLAY * ITEM_HEIGHT,
    paddingHorizontal: 12,
    paddingVertical: 8,
    ...shadow({
      color: theme.colors.black,
      elevation: 5,
      opacity: 0.2,
      radius: 4,
      x: 0,
      y: 2,
    }),
  },
  menuItem: {
    height: ITEM_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 8,
    width: '100%',
  },
  modalOverlay: {
    alignItems: 'flex-start',
    backgroundColor: theme.colors.transparent,
    flex: 1,
    justifyContent: 'flex-start',
    marginTop: 8,
  },
}));
